import { Avatar, AvatarFallback, AvatarImage } from '@attive/ui/avatar'
import { getUserImgSrc } from '#app/utils/misc'

interface UserAvatarProps {
	user: {
		name: string | null
		email?: string
		image?: { id: string } | null
	}
	className?: string
	size?: 'sm' | 'md' | 'lg'
}

// Colors for avatar backgrounds
const COLORS = [
	'bg-red-500',
	'bg-pink-500',
	'bg-purple-500',
	'bg-indigo-500',
	'bg-blue-500',
	'bg-cyan-500',
	'bg-teal-500',
	'bg-green-500',
	'bg-lime-500',
	'bg-amber-500',
	'bg-orange-500',
]

export function UserAvatar({
	user,
	className = '',
	size = 'md',
}: UserAvatarProps) {
	// Get initials (up to 2 characters)
	const getInitials = (name: string) => {
		// Split by spaces and get first character of each part
		return name
			.split(/\s+/)
			.map((part) => part[0])
			.filter(Boolean)
			.slice(0, 2)
			.join('')
			.toUpperCase()
	}

	// Get a deterministic color based on email or name
	const getColorClass = (identifier: string) => {
		// Simple hash function to get a number from a string
		const hash = identifier.split('').reduce((acc, char) => {
			return acc + char.charCodeAt(0)
		}, 0)

		// Use the hash to pick a color
		return COLORS[hash % COLORS.length]
	}

	const displayName = user.name || user.email || 'User'
	const initials = getInitials(displayName)
	const colorClass = getColorClass(user.email || displayName)

	// Size classes
	const sizeClasses = {
		sm: 'h-8 w-8',
		md: 'h-10 w-10',
		lg: 'h-16 w-16',
	}

	return (
		<Avatar className={`${sizeClasses[size]} ${className}`}>
			<AvatarImage src={getUserImgSrc(user.image?.id)} alt={displayName} />
			<AvatarFallback className={`${colorClass} font-medium text-white`}>
				{initials || '?'}
			</AvatarFallback>
		</Avatar>
	)
}
